import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { nightsPhrase } from '../utils/dates';
import { coverTypeStyle, isCoverColour } from '../utils/coverTypeColour';
import './OnCall.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu'];

function canAdmin(role) {
  return ['team_leader', 'manager', 'admin'].includes(role);
}

function dateKey(date) {
  return date.toISOString().slice(0, 10);
}

function getCalendarRange(year, month) {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const last = new Date(Date.UTC(year, month, 0));
  const start = new Date(first);
  const offset = (first.getUTCDay() - 5 + 7) % 7;
  start.setUTCDate(first.getUTCDate() - offset);
  const totalDays = offset + last.getUTCDate();
  const weeks = Math.ceil(totalDays / 7);
  const days = Array.from({ length: weeks * 7 }, (_, index) => {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + index);
    return day;
  });
  return { start, days, count: days.length };
}

function OnCallRota() {
  const { authFetch, user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [rota, setRota] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const [swapModal, setSwapModal] = useState(null);
  const [swapForm, setSwapForm] = useState({ proposed_engineer_id: '', reason: '' });
  const [mySwaps, setMySwaps] = useState([]);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentSummaryEnabled, setPaymentSummaryEnabled] = useState(false);
  const [mySummary, setMySummary] = useState(null);
  const role = user?.role || 'engineering';

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { start, count } = getCalendarRange(year, month);
    const params = new URLSearchParams({ view: 'list', start: dateKey(start), days: String(count) });
    const [rotaRes, engineersRes, swapsRes, visibilityRes] = await Promise.all([
      authFetch(`/api/v1/on-call/rota?${params}`),
      authFetch('/api/v1/on-call/engineers'),
      authFetch('/api/v1/on-call/swap-requests'),
      authFetch('/api/v1/on-call/my-visibility')
    ]);
    // A failed rota fetch must never render as a silently empty calendar -
    // that's how a 500 here went unnoticed for a week.
    if (rotaRes.ok) {
      setRota(await rotaRes.json());
    } else {
      setRota(null);
      setMessage({ type: 'error', text: 'The rota could not be loaded - please try again or contact an admin.' });
    }
    if (engineersRes.ok) setEngineers((await engineersRes.json()).data || []);
    if (swapsRes.ok) setMySwaps(((await swapsRes.json()).data || []).filter(r => r.requesting_engineer_id === user?.userId));
    if (visibilityRes.ok) {
      const vis = await visibilityRes.json();
      setPaymentSummaryEnabled(vis.payment_summary_enabled);
      if (vis.payment_summary_enabled) {
        const firstOfMonth = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastOfMonth = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
        const summaryRes = await authFetch(`/api/v1/on-call/my-summary?start=${firstOfMonth}&end=${lastOfMonth}`);
        if (summaryRes.ok) setMySummary(await summaryRes.json());
      }
    }
    setLoading(false);
  }, [authFetch, year, month, user?.userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const daysByDate = rota?.days || {};
  const warningsByDate = useMemo(() => {
    const result = {};
    (rota?.warnings || []).forEach(day => { result[day.date] = day.warnings; });
    return result;
  }, [rota]);

  const openSwapModal = (assignment) => {
    setSwapModal(assignment);
    setSwapForm({ proposed_engineer_id: '', reason: '' });
  };

  const submitSwap = async (event) => {
    event.preventDefault();
    const response = await authFetch('/api/v1/on-call/swap-requests', {
      method: 'POST',
      body: JSON.stringify({
        assignment_id: swapModal.assignment_id,
        proposed_engineer_id: swapForm.proposed_engineer_id,
        reason: swapForm.reason
      })
    });
    const data = await response.json();
    setMessage(data.message || (response.ok ? 'Swap request submitted' : 'Unable to submit swap'));
    if (response.ok) {
      setSwapModal(null);
    }
    fetchData();
  };

  const cancelSwap = async (requestId) => {
    const response = await authFetch(`/api/v1/on-call/swap-requests/${requestId}`, { method: 'DELETE' });
    if (response.ok) {
      setMessage('Swap request cancelled');
      fetchData();
    }
  };

  const prevMonth = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); }
    else setMonth(month - 1);
  };

  const nextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); }
    else setMonth(month + 1);
  };

  if (loading) {
    return (
      <div className="on-call-page">
        <div className="admin-loading">Loading on-call rota...</div>
      </div>
    );
  }

  return (
    <div className="on-call-page">
      <div className="on-call-header">
        <div>
          <h1>On Call Rota</h1>
          <p>View the on-call schedule and request shift swaps.</p>
        </div>
        <div className="on-call-actions">
          {canAdmin(role) && <Link className="on-call-btn secondary" to="/on-call/admin">Admin</Link>}
          {['admin', 'manager', 'financial'].includes(role) && <Link className="on-call-btn secondary" to="/on-call/payroll">Payroll</Link>}
        </div>
      </div>

      {/* message is a string or { type, text }: the failed-rota-fetch path sets
          the object, and rendering it raw blanked the whole page. */}
      {message && <div className={message.type === 'error' ? 'admin-error' : 'admin-info'}>{message.text || message}</div>}

      <section className="on-call-card">
        <div className="planner-toolbar">
          <div>
            <h2>{MONTHS[month - 1]} {year}</h2>
            <p className="on-call-muted">Rota weeks run Friday to Thursday. Click your assignment to request a swap.</p>
          </div>
          <div className="on-call-controls">
            <button className="on-call-btn secondary" onClick={prevMonth}>&larr; Prev</button>
            <button className="on-call-btn secondary" onClick={nextMonth}>Next &rarr;</button>
          </div>
        </div>

        {/* What each colour means: the colours are chosen on Rota Admin. */}
        {(rota?.cover_types || []).length > 0 && (
          <div className="cover-legend" aria-label="Cover types">
            {rota.cover_types.map(type => (
              <span key={type.cover_type_id} className="cover-legend-item">
                {isCoverColour(type.color) && <span className="cover-type-swatch" style={{ background: type.color }} />}
                {type.label}
              </span>
            ))}
          </div>
        )}

        <div className="admin-rota-grid">
          {WEEKDAYS.map(day => <div key={day} className="admin-rota-weekday">{day}</div>)}
          {getCalendarRange(year, month).days.map(day => {
            const key = dateKey(day);
            const assignments = daysByDate[key] || [];
            const warnings = warningsByDate[key] || [];
            const outside = day.getUTCMonth() !== month - 1;
            return (
              <div
                key={key}
                className={`admin-rota-day ${outside ? 'outside' : ''} ${warnings.length ? 'has-warning' : ''}`}
                style={{ cursor: 'default' }}
              >
                <div className="admin-rota-day-top">
                  <strong>{day.getUTCDate()}</strong>
                  {warnings.length > 0 && <span className="warning-dot">!</span>}
                </div>
                <div className="admin-rota-cover-list">
                  {assignments.map(item => {
                    const isMine = item.engineer_id === user?.userId;
                    return (
                      <div
                        key={item.assignment_id}
                        className={`admin-rota-cover ${item.engineer_id ? '' : 'missing'}`}
                        style={{
                          ...coverTypeStyle(item.cover_type_color, { assigned: Boolean(item.engineer_id) }),
                          ...(isMine ? { cursor: 'pointer', outline: '2px solid #1D9E75' } : {})
                        }}
                        onClick={isMine && !outside ? () => openSwapModal(item) : undefined}
                        title={isMine ? 'Click to request a swap' : ''}
                      >
                        <span>{item.cover_type_label}{item.cover_type_active === false ? ' (retired)' : ''}</span>
                        <strong>{item.engineer_name || 'Unassigned'}</strong>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {paymentSummaryEnabled && mySummary && (mySummary.data.length > 0 || mySummary.call_outs) && (
        <section className="on-call-card">
          <h2>My On-Call Payment Summary</h2>
          <p className="on-call-muted">{MONTHS[month - 1]} {year}</p>
          <table className="on-call-table">
            <thead>
              <tr><th>Cover Type</th><th>Day Type</th><th>Days</th><th>Rate</th><th>Total</th></tr>
            </thead>
            <tbody>
              {mySummary.data.map((row, index) => (
                <tr key={index}>
                  <td>{row.cover_type_label}</td>
                  <td>{row.day_type === 'weekday' ? 'Weekday' : row.day_type === 'weekend' ? 'Weekend' : 'Special Day'}</td>
                  <td>{row.assigned_days}</td>
                  <td>£{Number(row.standby_rate_gbp).toFixed(2)}</td>
                  <td>£{Number(row.standby_payment_gbp).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* B4: call-outs split into the ones from this month and the ones
              carried in late from a month that was already paid. The two lines
              add up to the totals beside them - late pay is part of the period
              total, never extra to it (US2). */}
          {mySummary.call_outs && (() => {
            const own = mySummary.call_outs;
            const num = (value, fallback) => (value === undefined || value === null ? Number(fallback || 0) : Number(value));
            const onTimeCount = num(own.on_time_incident_count, own.incident_count);
            const onTimeFees = num(own.on_time_incident_payment_gbp, own.incident_payment_gbp);
            const onTimeHours = num(own.on_time_overtime_hours, own.overtime_hours);
            const onTimeOvertime = num(own.on_time_estimated_overtime_gbp, own.estimated_overtime_gbp);
            const lateCount = num(own.late_incident_count, 0);
            const lateFees = num(own.late_incident_payment_gbp, 0);
            const lateHours = num(own.late_overtime_hours, 0);
            const lateOvertime = num(own.late_estimated_overtime_gbp, 0);
            const nights = [...new Set((mySummary.late_lines || []).map((line) => line.night_day_label || line.night))];
            return (
              <div className="on-call-muted" style={{ marginTop: '12px' }}>
                <p>
                  Call-outs on time: {onTimeCount} · £{onTimeFees.toFixed(2)}
                  {lateCount > 0 && (
                    <> · Late call-outs: {lateCount} · £{lateFees.toFixed(2)}
                      {nights.length > 0 && ` (${nightsPhrase(nights)})`}</>
                  )}
                  {' '}· Call-out total £{Number(own.incident_payment_gbp).toFixed(2)}
                </p>
                <p>
                  Out-of-hours on time: {onTimeHours.toFixed(2)}h · £{onTimeOvertime.toFixed(2)}
                  {lateHours > 0 && <> · Late: {lateHours.toFixed(2)}h · £{lateOvertime.toFixed(2)}</>}
                  {' '}· Overtime total £{Number(own.estimated_overtime_gbp).toFixed(2)}
                </p>
                {(mySummary.skipped_fees || []).map((skip, index) => (
                  <p key={`skipped-${index}`}>{skip.note}</p>
                ))}
                {(mySummary.times_changed_after_payment || []).map((changed, index) => (
                  <p key={`changed-${index}`}>{changed.note}</p>
                ))}
                {(mySummary.late_lines || []).length > 0 && (
                  <p>
                    Late items are call-outs from a pay period that was already finalised. They are paid in
                    the next open period, shown here only when this is that period.
                  </p>
                )}
                {mySummary.banner && <p>{mySummary.banner}</p>}
              </div>
            );
          })()}
          <p className="on-call-muted" style={{ marginTop: '12px' }}>
            <strong>
              Total standby payment: £{Number(mySummary.total_standby_gbp).toFixed(2)}
              {mySummary.total_gbp !== undefined && mySummary.total_gbp !== mySummary.total_standby_gbp
                ? ` · Total incl. call-outs: £${Number(mySummary.total_gbp).toFixed(2)}`
                : ''}
            </strong>
          </p>
        </section>
      )}

      {mySwaps.length > 0 && (
        <section className="on-call-card">
          <h2>My Swap Requests</h2>
          <table className="on-call-table">
            <thead>
              <tr><th>Date</th><th>Cover</th><th>Proposed</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {mySwaps.map(request => (
                <tr key={request.request_id}>
                  <td>{String(request.rota_date).slice(0, 10)}</td>
                  <td>{request.cover_type_label}</td>
                  <td>{request.proposed_engineer_name || '-'}</td>
                  <td>{request.reason}</td>
                  <td style={{ textTransform: 'capitalize' }}>{request.status}</td>
                  <td>
                    {request.status === 'pending' && (
                      <button className="on-call-btn danger" onClick={() => cancelSwap(request.request_id)}>Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {swapModal && (
        <div className="on-call-modal-backdrop" onClick={() => setSwapModal(null)}>
          <div className="on-call-modal" onClick={e => e.stopPropagation()}>
            <h2>Request Swap</h2>
            <p className="on-call-muted">
              {swapModal.cover_type_label} on {String(swapModal.rota_date).slice(0, 10)}
            </p>
            <form onSubmit={submitSwap}>
              <label>
                Proposed replacement
                <select
                  value={swapForm.proposed_engineer_id}
                  onChange={e => setSwapForm(prev => ({ ...prev, proposed_engineer_id: e.target.value }))}
                  required
                >
                  <option value="">Select replacement engineer</option>
                  {engineers.filter(e => e.user_id !== user?.userId).map(eng => (
                    <option key={eng.user_id} value={eng.user_id}>{eng.full_name}</option>
                  ))}
                </select>
              </label>
              <label>
                Reason
                <textarea
                  rows={3}
                  value={swapForm.reason}
                  onChange={e => setSwapForm(prev => ({ ...prev, reason: e.target.value }))}
                  required
                  placeholder="Please explain why you need to swap this shift..."
                />
              </label>
              <div className="on-call-actions">
                <button type="button" className="on-call-btn secondary" onClick={() => setSwapModal(null)}>Cancel</button>
                <button type="submit" className="on-call-btn primary">Submit Request</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default OnCallRota;
